import { Position } from "@xyflow/react";
import { ListPlus, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ContextualTipsTrigger } from "@babackapp/ui/components/contextual-tips";
import type { ActionNodeReturnOrderAttributeConfigDto } from "@babackapp/private-api-client/types";
import { BaseHandle } from "../../components/base-handle";
import { BaseNode, BaseNodeContent } from "../../components/base-node";
import { useNodeData } from "../../hooks/use-node-data";

export const RETURN_ORDER_ATTRIBUTE_NODE_WIDTH = 280;
export const RETURN_ORDER_ATTRIBUTE_NODE_HEIGHT_ESTIMATION = 120;

type LiveData = {
  actionType: string;
  config: ActionNodeReturnOrderAttributeConfigDto;
};

export function ReturnOrderAttributeActionNodeEditor({ id }: { id: string }) {
  const { t } = useTranslation("trackoffice");
  const liveData = useNodeData<LiveData>(id);
  const config =
    liveData?.actionType === "set_order_attribute" ? liveData.config : null;
  const isConfigured = !!config?.name && !!config?.value;

  return (
    <ContextualTipsTrigger
      tips={[
        {
          id: "workflow-node-return-order-attribute",
          Icon: ListPlus,
          title: t(
            "workflowsEditor.tips.workflowNodeOrderAttribute.title",
            "This is an order attribute action node",
          ),
          description: t(
            "workflowsEditor.tips.workflowNodeOrderAttribute.desc",
            "This node sets a custom attribute (name/value) on the Shopify order when the workflow runs.",
          ),
        },
      ]}
    >
      <BaseNode nodeId={id} style={{ position: "relative" }}>
        <BaseNodeContent>
          <div className="flex flex-col gap-1 w-full">
            <span className="text-xs text-muted-foreground">
              {t(
                "workflowsEditor.returnOrderAttribute.title",
                "Order attribute",
              )}
            </span>
            {isConfigured ? (
              <span className="text-xs mt-1 font-semibold break-words">
                {config.name} = {config.value}
              </span>
            ) : (
              <span className="text-xs mt-1 font-semibold text-amber-600 flex items-center gap-1">
                <TriangleAlert className="size-3 shrink-0" />
                {t(
                  "workflowsEditor.returnOrderAttribute.missing",
                  "Set a name and a value",
                )}
              </span>
            )}
          </div>
          <BaseHandle type="target" position={Position.Left} />
          <BaseHandle
            type="source"
            position={Position.Right}
            style={{ top: "50%", transform: "translateY(-50%)" }}
          />
        </BaseNodeContent>
      </BaseNode>
    </ContextualTipsTrigger>
  );
}
